<div class="dashboard">
  <!-- A plain div, not a `header`: inside `main` it would add a landmark that
       says nothing the heading does not already say. -->
  <div class="dashboard__header">
    <h1 class="dashboard__title">{{ 'dashboard.title' | translate }}</h1>

    @if (hasModules()) {
      <div class="dashboard__search">
        <label class="dashboard__search-label" for="module-search">
          {{ 'dashboard.search' | translate }}
        </label>
        <p-iconfield>
          <p-inputicon class="pi pi-search" />
          <input
            pInputText
            id="module-search"
            type="search"
            autocomplete="off"
            [placeholder]="'dashboard.search' | translate"
            [value]="search()"
            (input)="search.set($any($event.target).value)"
          />
        </p-iconfield>
      </div>
    }
  </div>

  <!-- UMC holds a founder on their business card until it is complete; every
       module stays closed until then, and this says where to finish it. -->
  @if (companies.cardIncomplete()) {
    <div class="dashboard__notice" role="status">
      <i class="pi pi-id-card" aria-hidden="true"></i>
      <div>
        <p class="dashboard__notice-title">{{ 'dashboard.cardIncomplete.title' | translate }}</p>
        <p class="dashboard__notice-text">{{ 'dashboard.cardIncomplete.text' | translate }}</p>
        @if (cardUrl; as url) {
          <a class="dashboard__notice-link" [href]="url">
            {{ 'dashboard.cardIncomplete.action' | translate }}
            <i class="pi pi-arrow-right" aria-hidden="true"></i>
          </a>
        }
      </div>
    </div>
  }

  @if (!licenses.settled()) {
    <!-- UMC has not answered yet: every verdict would read "open" for a
         moment, so the rows wait as skeletons instead of flashing links. -->
    <ul class="dashboard__grid" [attr.aria-busy]="true" [attr.aria-label]="'dashboard.loading' | translate">
      @for (row of skeletonRows(); track $index) {
        <li class="dashboard__skeleton" aria-hidden="true">
          <span class="dashboard__skeleton-icon"></span>
          <span class="dashboard__skeleton-text">
            <span class="dashboard__skeleton-line dashboard__skeleton-line--title"></span>
            <span class="dashboard__skeleton-line"></span>
          </span>
        </li>
      }
    </ul>
  } @else if (!hasModules()) {
    <p class="dashboard__empty">{{ 'dashboard.empty' | translate }}</p>
  } @else if (visibleCards().length === 0) {
    <p class="dashboard__empty">{{ 'dashboard.noMatch' | translate: { term: search() } }}</p>
  } @else {
    <!-- A list, so a screen reader announces how many modules there are. -->
    <ul class="dashboard__grid">
      @for (card of visibleCards(); track card.id) {
        <li class="dashboard__item">
          <app-module-card [module]="card" (licenseInfo)="openLicenseInfo(card)" />
        </li>
      }
    </ul>
  }
</div>

<!-- Licence details for one module: the verdict, the company, what the UMC
     catalogue and the company's licence say. Opened from the "i" beside a
     tile's licence pill. -->
@if (licenseInfoOf(); as card) {
  <p-dialog
    [visible]="true"
    (visibleChange)="licenseInfoOf.set(null)"
    [modal]="true"
    [dismissableMask]="true"
    [draggable]="false"
    [style]="{ width: '28rem', 'max-width': '92vw' }"
    [header]="'module.license.info.title' | translate"
  >
    <dl class="license-info">
      <dt>{{ 'module.license.info.module' | translate }}</dt>
      <dd>{{ card.titleKey | translate }}</dd>

      @if (companies.activeCustomerName(); as customerName) {
        <dt>{{ 'module.license.info.customer' | translate }}</dt>
        <dd>{{ customerName }}</dd>
      }

      <dt>{{ 'module.license.info.company' | translate }}</dt>
      <dd>{{ companies.activeCompanyName() ?? '—' }}</dd>

      <dt>{{ 'module.license.info.state' | translate }}</dt>
      <dd>
        <span class="license-info__pill license-info__pill--{{ card.license ?? 'none' }}">
          {{ 'module.license.' + (card.license ?? 'none') + '.label' | translate }}
        </span>
      </dd>

      @if (licenseInfoCatalog(); as entry) {
        <dt>{{ 'module.license.info.catalogName' | translate }}</dt>
        <dd>{{ entry.moduleLabel }} ({{ entry.moduleCode }})</dd>

        @if (licenses.licenseDetailOf(card.id); as licence) {
          @if (licence.licenseName; as name) {
            <dt>{{ 'module.license.info.licName' | translate }}</dt>
            <dd>{{ name }}</dd>
          }
          @if (licence.licensePublicId; as licenceId) {
            <dt>{{ 'module.license.info.licGuid' | translate }}</dt>
            <dd class="license-info__guid">{{ licenceId }}</dd>
          }

          <dt>{{ 'module.license.info.licStatus' | translate }}</dt>
          <dd>{{ licence.licenseStatus }}</dd>

          <dt>{{ 'module.license.info.coverage' | translate }}</dt>
          <dd>
            @if (licence.assignmentMode === 'users') {
              {{
                'module.license.info.seats'
                  | translate: { used: licence.seatsUsed ?? 0, limit: licence.seatsLimit ?? '∞' }
              }}
            } @else {
              {{ 'module.license.info.entireCompany' | translate }}
            }
          </dd>

          @if (licence.startsAt; as from) {
            <dt>{{ 'module.license.info.validFrom' | translate }}</dt>
            <dd>{{ from | date: 'mediumDate' }}</dd>
          }
          @if (licence.expiresAt; as until) {
            <dt>{{ 'module.license.info.validUntil' | translate }}</dt>
            <dd>{{ until | date: 'mediumDate' }}</dd>
          }
        }

        <dt>{{ 'module.license.info.guid' | translate }}</dt>
        <dd class="license-info__guid">{{ entry.modulePublicId }}</dd>
      } @else {
        <dt>{{ 'module.license.info.catalogName' | translate }}</dt>
        <dd>{{ 'module.license.info.noCatalog' | translate }}</dd>
      }
    </dl>

    <p class="license-info__hint">
      {{ 'module.license.' + (card.license ?? 'none') + '.hint' | translate }}
    </p>

    <!-- The full service page: everything above plus version and GUIDs, as an
         address that can be pasted into a support ticket. -->
    <a class="license-info__page" [routerLink]="['/', modulePrefix, 'info', card.id]">
      {{ 'module.info.open' | translate }}
      <i class="pi pi-arrow-right" aria-hidden="true"></i>
    </a>
  </p-dialog>
}
